import {
  getPlatformV1AuthProviders,
  getPlatformV1Auth2fa,
  postPlatformV1Auth2faDisable,
  postPlatformV1Auth2faEnable,
  postPlatformV1Auth2faRecoveryCodes,
  postPlatformV1Auth2faSetup,
  postPlatformV1Auth2faVerify,
  postPlatformV1AuthGoogleCallback,
  postPlatformV1AuthGoogleStart,
  postPlatformV1AuthLogin,
  postPlatformV1AuthMagicLink,
  postPlatformV1AuthMagicLinkVerify,
  postPlatformV1AuthPasswordForgot,
  postPlatformV1AuthPasswordReset,
  type ForgotPasswordRequest,
  type GoogleCallbackRequest,
  type GoogleStart,
  type GoogleStartRequest,
  type SignInProviders,
  type MagicLinkRequest,
  type MagicLinkVerifyRequest,
  type PlatformLoginRequest,
  type ResetPasswordRequest,
  type TokenResponse,
  type TwoFactorChallenge,
  type TwoFactorCodeRequest,
  type TwoFactorDisableRequest,
  type TwoFactorEnabled,
  type TwoFactorSetup,
  type TwoFactorStatus,
  type TwoFactorVerifyRequest,
} from '@nucleus-commerce/api-client/platform';

/**
 * Login, magic-link verify and the Google callback answer 200 with tokens or 202 with a
 * two-factor challenge.
 */
export type SignInResult = TokenResponse | TwoFactorChallenge;

/**
 * Thin wrapper over the generated client. Login, two-factor verify, magic-link verify and the
 * Google callback set the `nucleus_platform` HttpOnly cookie; the access token in the body exists for API clients and
 * the browser app never stores it. The public endpoints skip the 401 hook: a wrong code or an
 * expired link is a form error, not a lost session. The /auth/2fa management calls run inside a
 * session and keep the hook.
 */
export const authApi = {
  login: (body: PlatformLoginRequest): Promise<SignInResult> =>
    postPlatformV1AuthLogin(body, { skipUnauthorizedHook: true }),
  verifyTwoFactor: (body: TwoFactorVerifyRequest): Promise<TokenResponse> =>
    postPlatformV1Auth2faVerify(body, { skipUnauthorizedHook: true }),
  requestMagicLink: (body: MagicLinkRequest): Promise<void> =>
    postPlatformV1AuthMagicLink(body, { skipUnauthorizedHook: true }),
  verifyMagicLink: (body: MagicLinkVerifyRequest): Promise<SignInResult> =>
    postPlatformV1AuthMagicLinkVerify(body, { skipUnauthorizedHook: true }),
  forgotPassword: (body: ForgotPasswordRequest): Promise<void> =>
    postPlatformV1AuthPasswordForgot(body, { skipUnauthorizedHook: true }),
  resetPassword: (body: ResetPasswordRequest): Promise<void> =>
    postPlatformV1AuthPasswordReset(body, { skipUnauthorizedHook: true }),
  providers: (): Promise<SignInProviders> =>
    getPlatformV1AuthProviders({ skipUnauthorizedHook: true }),
  googleStart: (body: GoogleStartRequest): Promise<GoogleStart> =>
    postPlatformV1AuthGoogleStart(body, { skipUnauthorizedHook: true }),
  googleCallback: (body: GoogleCallbackRequest): Promise<SignInResult> =>
    postPlatformV1AuthGoogleCallback(body, { skipUnauthorizedHook: true }),
};

export const twoFactorApi = {
  status: (): Promise<TwoFactorStatus> => getPlatformV1Auth2fa(),
  setup: (): Promise<TwoFactorSetup> => postPlatformV1Auth2faSetup(),
  enable: (body: TwoFactorCodeRequest): Promise<TwoFactorEnabled> =>
    postPlatformV1Auth2faEnable(body),
  regenerateRecoveryCodes: (body: TwoFactorCodeRequest): Promise<TwoFactorEnabled> =>
    postPlatformV1Auth2faRecoveryCodes(body),
  disable: (body: TwoFactorDisableRequest): Promise<void> => postPlatformV1Auth2faDisable(body),
};
